/**
 * Stable identifiers for app modules and user roles.
 *
 * ## APP_MODULE_KEYS (derived)
 *
 * Built at module load from `registryEntries` (see `appModuleDefinitions.js`).
 * Enum name = `key.toUpperCase()` with non-alphanumerics → `_`
 * (e.g. `"Dashboard"` → `APP_MODULE_KEYS.DASHBOARD === "Dashboard"`).
 *
 * Do **not** hand-maintain the enum when adding a module — add a `key` string in
 * `appModuleDefinitions.js` and import `APP_MODULE_KEYS` in application code.
 *
 * ## USER_ROLES (explicit)
 *
 * Canonical `userData.role` strings for access policy. Not derived from the registry
 * because roles are auth concerns, not module metadata.
 *
 * @module modules/appModuleKeys
 * @see docs/Modules/module-access.md
 */

import { registryEntries } from "./appModuleDefinitions";

/** PascalCase module ids aligned with backend `UserModule` / `userData.modules`. */
export const APP_MODULE_KEYS = Object.freeze(
  Object.fromEntries(
    registryEntries.map(({ key }) => [
      key.toUpperCase().replace(/[^A-Z0-9]+/g, "_").replace(/^_|_$/g, ""),
      key,
    ])
  )
);

/** Canonical `userData.role` values used by the access policy. */
export const USER_ROLES = Object.freeze({
  OWNER: "owner",
  ADMIN: "admin",
  USER: "user",
  EVALUATOR: "evaluator",
});

/** All valid module key strings — used for case-insensitive normalization. */
export const APP_MODULE_KEY_VALUES = Object.freeze(Object.values(APP_MODULE_KEYS));

/**
 * Resolve a loose module string to a canonical registry key.
 *
 * @param {unknown} moduleKey — value from API, registry, or route guard
 * @returns {string|undefined} PascalCase key from {@link APP_MODULE_KEYS}, or `undefined`
 */
export function normalizeAppModuleKey(moduleKey) {
  if (typeof moduleKey !== "string") return undefined;
  const normalized = moduleKey.trim().toLowerCase();
  return APP_MODULE_KEY_VALUES.find((key) => key.toLowerCase() === normalized);
}
